package com.gastoncantera.intercom.call

/** Motivo por el que falló el acceso a micrófono/cámara al iniciar o contestar una llamada. */
enum class CallError { PermissionDenied, DeviceNotFound, Generic }

/** Permiso denegado detectado ANTES de pedir media (lo lanza iOS con AVCaptureDevice). */
class MediaPermissionDeniedException : Exception("media permission denied")

/**
 * iOS chequea/solicita permisos explícitamente y lanza [MediaPermissionDeniedException] si faltan
 * (en iOS getUserMedia no falla al negar: degrada a media vacía). Android y web no hacen nada acá:
 * el SO / navegador gestionan el prompt y el fallo llega por `getUserMedia` (excepción tipada en
 * Android, `DOMException` en web).
 *
 * [withVideo] respeta el modo: el portero pide mic + cámara; el usuario solo micrófono.
 */
expect suspend fun requireMediaPermission(withVideo: Boolean)

/** Clasificación específica por plataforma de la excepción de `getUserMedia`. */
internal expect fun classifyPlatformMediaError(e: Throwable): CallError

fun classifyMediaError(e: Throwable): CallError =
    if (e is MediaPermissionDeniedException) CallError.PermissionDenied
    else classifyPlatformMediaError(e)

fun CallError.friendlyMessage(withVideo: Boolean): String = when (this) {
    CallError.PermissionDenied -> if (withVideo) {
        "Necesitamos permiso para usar el micrófono y la cámara. Habilitalos en los ajustes y volvé a intentar."
    } else {
        "Necesitamos permiso para usar el micrófono. Habilitalo en los ajustes y volvé a intentar."
    }
    CallError.DeviceNotFound -> if (withVideo) {
        "No encontramos micrófono o cámara en este dispositivo."
    } else {
        "No encontramos micrófono en este dispositivo."
    }
    CallError.Generic -> "No se pudo iniciar la llamada. Revisá los permisos e intentá de nuevo."
}
